import React from 'react';
import styles from './Text.module.css';

export const TYPOGRAPHY_CLASSES = {
  h1: 'heading-1',
  h2: 'heading-2',
  h3: 'heading-3',
  h4: 'heading-4',
  h5: 'heading-5',
  h6: 'heading-6',
  p: 'body',
  small: 'small',
  strong: 'strong',
} as const;

type ValidHtmlElement =
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'p'
  | 'span'
  | 'div'
  | 'section'
  | 'article'
  | 'aside'
  | 'header'
  | 'footer'
  | 'main'
  | 'strong'
  | 'em'
  | 'small'
  | 'mark'
  | 'del'
  | 'ins'
  | 'sub'
  | 'sup'
  | 'button';

type TypographyElement = keyof typeof TYPOGRAPHY_CLASSES;

interface TextProps<T extends ValidHtmlElement = 'p'> {
  as?: T;
  className?: string;
  children: React.ReactNode;
}

type Props<T extends ValidHtmlElement = 'p'> = TextProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof TextProps<T>>;

const isTypographyElement = (element: ValidHtmlElement): element is TypographyElement => element in TYPOGRAPHY_CLASSES;

const getTypographyClass = (element: ValidHtmlElement): string => {
  if (isTypographyElement(element)) {
    return styles[TYPOGRAPHY_CLASSES[element]];
  }
  return styles[TYPOGRAPHY_CLASSES.p];
};

const Text = <T extends ValidHtmlElement = 'p'>({
  as = 'p' as T,
  className = '',
  children,
  ...props
}: Props<T>): React.ReactElement => {
  const typographyClass = getTypographyClass(as);
  const computedClassName = [typographyClass, className].filter(Boolean).join(' ');

  return React.createElement(as, { className: computedClassName, ...props }, children);
};

export default Text;
export type { Props as TextProps, ValidHtmlElement };
